// EventChat — full-screen event chat thread (Sophi App v2 Piece 1).
//
// Polls /chat/:eventId/messages every 5s while visible; pauses when
// document.hidden. Defers Firestore client-SDK realtime to v3 to keep the
// bundle small. Optimistic-append on send; server response replaces the
// optimistic doc when its real id lands.
//
// Wired from Eventos.tsx via setChatEventId(eventId). The App-level state
// covers the screen (hides the tab bar) until user backs out.

import { useEffect, useRef, useState } from 'preact/hooks';
import {
  getChatThread, getChatMessages, postChatMessage, markChatRead,
  updateChatBriefing, getUser, ApiError,
  type ChatMessage, type ChatThread,
} from '../lib/api';
import { haptic } from '../lib/telegram';
import { Linkify } from '../components/Linkify';

const POLL_INTERVAL_MS = 5000;
const HOUR_MS = 3600_000;

// How long the "you're no longer a member" banner stays before auto-closing
// the chat screen. Long enough to read, short enough not to feel stuck.
const EVICTION_AUTO_CLOSE_MS = 4000;

function isEvictionError(e: unknown): boolean {
  // Server returns 403 with error='not_a_member' when computeMembership no
  // longer includes this staff (cancel / swap accept / admin remove flipped
  // their Confirmaciones_Personal.Estado). Exact-match the code: 403 also
  // arrives as 'chat_not_open' (pre-event write gate), which must NOT trigger
  // the eviction farewell.
  return e instanceof ApiError && e.status === 403 && e.message === 'not_a_member';
}

function isChatClosedError(e: unknown): boolean {
  // Pre-event write gate: only the captain and Ops may post before the
  // event's date. Reads are unaffected.
  return e instanceof ApiError && e.status === 403 && e.message === 'chat_not_open';
}

// "2026-06-20" → "viernes 20 de junio". Noon-AST anchor avoids the off-by-one
// a bare Date('YYYY-MM-DD') (UTC midnight) causes on UTC-4 devices. Same
// approach as EventDetailModal.formatLongDate.
const MONTH_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DAY_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
function formatOpensOn(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const d = new Date(`${iso}T12:00:00-04:00`);
  if (isNaN(d.getTime())) return iso;
  return `${DAY_ES[d.getDay()]} ${parseInt(m[3], 10)} de ${MONTH_ES[d.getMonth()]}`;
}

interface EventChatProps {
  eventId: string;
  eventTitle?: string;
  onClose: () => void;
}

export function EventChat({ eventId, eventTitle, onClose }: EventChatProps) {
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Briefing stays pinned open by default — staff shouldn't have to discover
  // a collapsed toggle to find the meeting point. Still collapsible by tap.
  const [briefingOpen, setBriefingOpen] = useState(true);
  /** True when the server returned 403/not_a_member on a chat call — staff has
   *  been removed (cancel, swap, admin remove) while viewing the thread.
   *  Triggers a 4s farewell banner then auto-close. */
  const [evicted, setEvicted] = useState(false);
  // Admin briefing editor (✏️ on the briefing block).
  const [editingBriefing, setEditingBriefing] = useState(false);
  const [briefingDraft, setBriefingDraft] = useState('');
  const [briefingSaving, setBriefingSaving] = useState(false);
  const [briefingError, setBriefingError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const me = getUser();
  const myId = me?.personalId || '';
  const isAdmin = !!me?.isAdmin;
  // Pre-event the composer is locked for regular staff (server-computed;
  // undefined = older API = open). UX gate only — the server enforces.
  const canPost = thread ? thread.canPost !== false : true;
  // Admins and the event's captain own the pinned briefing/notes. UX gate
  // only — the server re-checks on PUT.
  const canEditBriefing = isAdmin || (!!myId && thread?.capitanId === myId);

  // Auto-close after the eviction banner has been visible long enough to read.
  useEffect(() => {
    if (!evicted) return;
    const t = setTimeout(() => onClose(), EVICTION_AUTO_CLOSE_MS);
    return () => clearTimeout(t);
  }, [evicted, onClose]);

  // Load thread metadata + first page of messages.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, m] = await Promise.all([
          getChatThread(eventId),
          getChatMessages(eventId, { limit: 50 }),
        ]);
        if (cancelled) return;
        setThread(t);
        // listMessages returns newest-first; reverse for natural top-to-bottom display.
        setMessages(m.messages.slice().reverse());
      } catch (e) {
        if (cancelled) return;
        if (isEvictionError(e)) setEvicted(true);
        else setError((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // Auto-scroll to bottom on new messages (but only if user is already near the bottom).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Poll for new messages while visible.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    async function tick() {
      if (document.hidden) { timer = setTimeout(tick, POLL_INTERVAL_MS); return; }
      try {
        const r = await getChatMessages(eventId, { limit: 50 });
        if (cancelled) return;
        const fresh = r.messages.slice().reverse();
        setMessages((prev) => {
          // Cheap delta: if last id changed or count differs, replace. Avoids deep diff.
          if (prev.length !== fresh.length) return fresh;
          const last = prev[prev.length - 1];
          const lastFresh = fresh[fresh.length - 1];
          return last?.id !== lastFresh?.id ? fresh : prev;
        });
        // canPost rides the poll so a locked composer heals at the midnight
        // flip (or after a captain change) without reopening the screen.
        if (typeof r.canPost === 'boolean') {
          setThread((t) => {
            if (!t || t.canPost === r.canPost) return t;
            return { ...t, canPost: r.canPost, opensOn: r.canPost ? null : t.opensOn };
          });
        }
      } catch (e) {
        if (isEvictionError(e)) {
          // Membership lost mid-session (someone cancelled or was removed).
          // Stop polling; the eviction banner + auto-close effect takes over.
          if (!cancelled) setEvicted(true);
          return;
        }
        // Transient errors — swallow, next tick retries.
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    }

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [eventId]);

  // Mark latest message as read whenever the messages list updates.
  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (!last.id) return;
    markChatRead(eventId, last.id).catch(() => { /* best-effort */ });
  }, [eventId, messages.length]);

  async function send(e: Event) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    haptic('light');

    // Optimistic append. Tagged with kind='_pending' so the bubble can show a state hint.
    const tempId = `tmp-${Date.now()}`;
    const optimistic: ChatMessage = {
      id: tempId,
      authorPersonalId: myId,
      // Own messages don't render an author label, but set it for parity with
      // the server copy that replaces this optimistic bubble on send success.
      authorName: me?.nombre || me?.firstName || myId,
      body,
      kind: 'text',
      createdAt: new Date().toISOString(),
      isCapitan: thread?.capitanId === myId,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');

    try {
      const r = await postChatMessage(eventId, body);
      // Replace the optimistic message with the server's authoritative copy.
      setMessages((prev) => prev.map((m) => m.id === tempId ? r.message : m));
    } catch (err) {
      // Race: staff tapped Send during the millisecond their cancel/removal
      // committed. lib/chat.js postMessage re-checks membership and rejects
      // with 403. Drop into the eviction flow instead of showing a generic
      // error.
      if (isEvictionError(err)) {
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
        setEvicted(true);
        return;
      }
      // Pre-event write gate caught a stale client (opened before the gate
      // state was known). Lock the composer locally and keep the draft.
      if (isChatClosedError(err)) {
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
        setDraft(body);
        setError('El chat se abre el día del evento. Solo el capitán y Operaciones pueden escribir antes.');
        setThread((t) => t ? { ...t, canPost: false } : t);
        return;
      }
      setError((err as Error).message || 'No pude enviar el mensaje');
      // Remove the failed optimistic message so the user can retry.
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setDraft(body); // restore so they can edit + retry
    } finally {
      setSending(false);
    }
  }

  async function saveBriefing(e: Event) {
    e.preventDefault();
    const text = briefingDraft.trim();
    // Empty text is only meaningful as "volver al automático" — when the
    // current briefing is already the auto one, there's nothing to clear.
    if (!text && !thread?.briefingIsCustom) {
      setBriefingError('El briefing no puede estar vacío.');
      return;
    }
    if (briefingSaving) return;
    setBriefingSaving(true);
    setBriefingError(null);
    try {
      const r = await updateChatBriefing(eventId, text);
      setThread((t) => t ? { ...t, briefing: r.briefing, briefingIsCustom: r.isCustom } : t);
      setEditingBriefing(false);
      setBriefingOpen(true);
      haptic('light');
      // The "📋 Briefing actualizado" system message arrives via the 5s poll.
    } catch {
      setBriefingError('No pude actualizar el briefing. Intenta de nuevo.');
    } finally {
      setBriefingSaving(false);
    }
  }

  if (evicted) {
    // Replaced the chat surface with a small farewell card. Auto-closes
    // after EVICTION_AUTO_CLOSE_MS so the staff member isn't stuck staring
    // at a dead screen.
    return (
      <div class="event-chat-screen">
        <header class="event-chat-header">
          <button class="event-chat-back" onClick={onClose} aria-label="Cerrar">←</button>
          <div class="event-chat-titles">
            <div class="event-chat-title">{eventTitle || eventId}</div>
            <div class="event-chat-sub">Saliendo del chat…</div>
          </div>
        </header>
        <div class="event-chat-evicted">
          <div class="event-chat-evicted-icon">👋</div>
          <div class="event-chat-evicted-title">Ya no perteneces a este evento</div>
          <div class="event-chat-evicted-hint">
            Tu asignación cambió. Si fue un error, contacta a Operaciones.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="event-chat-screen">
      <header class="event-chat-header">
        <button class="event-chat-back" onClick={onClose} aria-label="Cerrar chat">←</button>
        <div class="event-chat-titles">
          <div class="event-chat-title">{eventTitle || eventId}</div>
          <div class="event-chat-sub">
            {thread ? `${thread.memberCount} en el equipo` : 'Cargando equipo...'}
          </div>
        </div>
      </header>

      {thread && (thread.briefing || canEditBriefing) && (
        <div class={`event-chat-briefing ${briefingOpen ? 'open' : ''}`}>
          <div class="event-chat-briefing-bar">
            <button class="event-chat-briefing-toggle" onClick={() => setBriefingOpen(!briefingOpen)}>
              📌 Briefing del evento {briefingOpen ? '▴' : '▾'}
            </button>
            {canEditBriefing && (
              <button
                class="event-chat-briefing-edit"
                onClick={() => {
                  setBriefingDraft(thread.briefing || '');
                  setBriefingError(null);
                  setEditingBriefing(true);
                }}
              >
                ✏️ Editar
              </button>
            )}
          </div>
          {briefingOpen && (
            thread.briefing
              ? <pre class="event-chat-briefing-text"><Linkify text={thread.briefing} /></pre>
              : <div class="event-chat-briefing-empty">Este evento aún no tiene briefing. Escríbelo aquí.</div>
          )}
        </div>
      )}

      <div class="event-chat-messages" ref={scrollRef}>
        {messages.length === 0 && !error && (
          <div class="event-chat-empty">Aún no hay mensajes. Sé el primero en saludar al equipo.</div>
        )}
        {messages.map((m, i) => {
          if (m.kind === 'system') {
            // System messages render as a centered notice — membership
            // transitions ("Mariana canceló", "Bienvenida Yarisol"), not
            // user-typed content. No author bubble.
            return <SystemNotice key={m.id} m={m} />;
          }
          const prev = messages[i - 1];
          const showAuthor = !prev || prev.authorPersonalId !== m.authorPersonalId;
          const mine = m.authorPersonalId === myId;
          return <MessageBubble key={m.id} m={m} showAuthor={showAuthor} mine={mine} />;
        })}
      </div>

      {error && (
        <div class="event-chat-error">⚠️ {error}</div>
      )}

      {canPost ? (
        <form class="event-chat-composer" onSubmit={send}>
          <textarea
            rows={1}
            value={draft}
            onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(e);
              }
            }}
            placeholder="Escribe un mensaje al equipo..."
            disabled={sending}
          />
          <button type="submit" class="event-chat-send" disabled={sending || !draft.trim()}>
            ➤
          </button>
        </form>
      ) : (
        <div class="event-chat-locked">
          🔒 Solo el capitán y Operaciones pueden escribir antes del evento.
          {' '}El chat se abre el {thread?.opensOn ? formatOpensOn(thread.opensOn) : 'día del evento'}.
        </div>
      )}

      {editingBriefing && (
        <div class="modal-bg show" onClick={(e) => { if (e.target === e.currentTarget && !briefingSaving) setEditingBriefing(false); }}>
          <div class="modal">
            <div class="modal-body">
              <h2>✏️ Editar briefing</h2>
              <div class="event-chat-briefing-hint">
                {thread?.briefingIsCustom
                  ? 'Briefing personalizado. Borra todo el texto y guarda para volver al briefing automático (datos del evento + equipo).'
                  : 'Este briefing se genera automático con los datos del evento y el equipo. Edítalo para personalizarlo.'}
              </div>
              <textarea
                class="event-chat-briefing-input"
                rows={10}
                maxLength={4000}
                value={briefingDraft}
                onInput={(e) => setBriefingDraft((e.target as HTMLTextAreaElement).value)}
                placeholder="Punto de encuentro, vestimenta, hora de llegada, notas para el equipo..."
                disabled={briefingSaving}
              />
              {briefingError && <div class="event-chat-error">⚠️ {briefingError}</div>}
              <div class="modal-actions">
                <button class="btn ghost" onClick={() => setEditingBriefing(false)} disabled={briefingSaving}>
                  Cancelar
                </button>
                <button class="btn primary" onClick={saveBriefing} disabled={briefingSaving || (!briefingDraft.trim() && !thread?.briefingIsCustom)}>
                  {briefingSaving ? 'Guardando…' : (!briefingDraft.trim() && thread?.briefingIsCustom ? 'Volver al automático' : 'Guardar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SystemNotice({ m }: { m: ChatMessage }) {
  const time = formatRelativeTime(m.createdAt);
  return (
    <div class="msg-system">
      <div class="msg-system-body">{m.body}</div>
      {time && <div class="msg-system-time">{time}</div>}
    </div>
  );
}

function MessageBubble({ m, showAuthor, mine }: { m: ChatMessage; showAuthor: boolean; mine: boolean }) {
  const time = formatRelativeTime(m.createdAt);
  return (
    <div class={`msg ${mine ? 'msg-mine' : 'msg-theirs'} ${m.isCapitan ? 'msg-capitan' : ''}`}>
      {showAuthor && !mine && (
        <div class="msg-author">
          {m.isCapitan && <span class="badge-role badge-captain">👑 Capitán</span>}
          <span class="msg-author-id">{m.authorName || m.authorPersonalId}</span>
        </div>
      )}
      <div class="msg-bubble">
        <span class="msg-body">{m.body}</span>
        <span class="msg-time">{time}</span>
      </div>
    </div>
  );
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'ahora';
  if (diff < HOUR_MS) return `hace ${Math.floor(diff / 60_000)} min`;
  if (diff < 24 * HOUR_MS) return `hace ${Math.floor(diff / HOUR_MS)} h`;
  return new Date(iso).toLocaleString('es-DO', {
    hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short',
  });
}
